import { assignReadOnly, isTraits, warn } from '../utils';
import { entityIdAndTypeMatch, getAndSetAvtSafe } from './validate-identity';
import { EntityTypes } from '../enums';
// an abstraction for getting/setting user attributes to cookies or localStorage
import { PersistentStore } from './persistant-store';
/**
 * The only reason we're not using a private property is because of the size
 * that would add to the bundle when compiled. It really doesn't matter, though,
 * if this is shared between instances of `User` because it's just a wrapper
 * around cookies and localStorage.
 */
let store;
export class User {
    // set default entity, id, and traits
    constructor({ apiKey }) {
        // Reinitializing in the constructor is easier
        // to write tests with a fresh store instance.
        // This makes no difference to the functionality
        // of the persistentStore in actual use
        store = new PersistentStore({ apiKey });
        // try to find entityType and id from storage
        const id = store.getEntityId();
        const entityType = store.getEntityType();
        const traits = store.getEntityTraits();
        this._identify(id, entityType, traits);
    }
    _setEntityId(passedId) {
        assignReadOnly(this, 'entityId', passedId);
        store.setEntityId(passedId, this.entityType);
    }
    _setEntityType(passedEntity) {
        assignReadOnly(this, 'entityType', passedEntity);
        store.setEntity(passedEntity);
    }
    _setEntityTraits(passedTraits) {
        const newTraits = Object.assign(Object.assign({}, this.entityTraits), passedTraits);
        assignReadOnly(this, 'entityTraits', newTraits);
        store.setEntityTraits(newTraits);
    }
    /**
     * Set user identity without making a network call.
     * Useful for testing.
     */
    _identify(id, type, traits) {
        if (entityIdAndTypeMatch(id, type)) {
            // entity needs to be set before id as setId require to know entity
            this._setEntityType(type);
            if (type === EntityTypes.anonVisitor) {
                // _savt is always the source of truth for anonymous_visitor
                this._setEntityId(getAndSetAvtSafe(id));
            }
            else {
                this._setEntityId(id);
            }
            // if entity and type are mismatched and identify
            // was not previously called (hence entity and id
            // being falsey), default to anonymous and AVT. Rely
            // on a new identify call in application logic to fix.
        }
        else if (!this.entityType || !this.entityId) {
            this._setEntityType(EntityTypes.anonVisitor);
            this._setEntityId(getAndSetAvtSafe());
        }
        // even if user identity is a mismatch,
        // we should still set traits
        if (!traits)
            return;
        if (isTraits(traits)) {
            this._setEntityTraits(traits);
        }
        else {
            warn("The third argument traits is not of type 'object'. Traits were not set.");
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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